import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Input, Radio, Select } from 'antd';

import AlertComponent from 'components/Alert';
import CharacterCounter from 'components/CharacterCounter';
import CropUploader from 'components/CropUploader';
import { removeDoubleSpace } from 'helpers/functions';
import { EChargeType } from 'interfaces/IEditProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import type IProduct from 'interfaces/IProduct';
import { FormContent } from 'scenes/Producer/Product/EditProduct/components/FormContent';
import { EProductType } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import Validations from './validations';

interface IProps {
  product: IProduct;
  handleChange: (...args: any[]) => any;
  categoryList: Array<any>;
  setRefField: any;
  setRefError: any;
  validateTitleForSave: any;
  productTitleExists: boolean;
  enabledValidation: boolean;
  categorySelected?: any;
  hasDeliveries?: boolean;
  resetDeliveries?: () => void;
  handleProductType?: (...args: any[]) => any;
}

function General(props: IProps) {
  const {
    handleChange,
    product,
    categoryList,
    setRefField,
    validateTitleForSave,
    enabledValidation,
    hasDeliveries,
    resetDeliveries,
    handleProductType
  } = props;

  const [validationErrors, setValidationErrors] = useState<Array<any>>([]);
  const prevPropsRef = useRef({ enabledValidation, product });

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isInvalid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const handleProductTypeChange = useCallback(
    async (e: any) => {
      if (hasDeliveries) {
        const confirm = await AlertComponent.confirm({
          title: 'Atenção',
          message: 'Ao alterar o tipo de produto, você perderá todas as entregas cadastradas. Deseja continuar?'
        });
        if (confirm) resetDeliveries!();
      }

      handleProductType!('type', e.target.value);
    },
    [hasDeliveries, resetDeliveries, handleProductType]
  );

  useEffect(() => {
    const prev = prevPropsRef.current;
    const shouldRun =
      (prev.enabledValidation === false && enabledValidation) || (enabledValidation && product !== prev.product);
    if (shouldRun) setValidationErrors(run(product, Validations));
    prevPropsRef.current = { enabledValidation, product };
  }, [enabledValidation, product]);

  const isService = product.cnt_partner_cod === EPartnerCode.SERVICE || product.type === EProductType.SERVICE;
  const categories = Array.isArray(categoryList) ? categoryList : [];
  const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;
  const isBlinket = product.cnt_partner_cod === EPartnerCode.BLINKET;
  const userHasAccessToSubscriptionModule = eduzzAccountsService.verifyUserHasBetaModule('planos_assinatura');

  function formTitle() {
    if (isSubscription) {
      return 'Dados básicos da assinatura';
    }

    if (isService) {
      return 'Dados básicos do serviço';
    }

    return 'Dados básicos do infoproduto';
  }

  return (
    <FormContent title={formTitle()} loading={false}>
      <div className='flex flex-col gap-4'>
        <div>
          {isSubscription && (userHasAccessToSubscriptionModule || !!handleProductType) && (
            <>
              <label className='mb-2 block font-medium'>
                Você está criando um plano de assinatura para um produto ou serviço?
              </label>
              <Radio.Group
                onChange={handleProductTypeChange}
                value={
                  product.type === EProductType.DIGITAL
                    ? 'product'
                    : product.type === EProductType.SERVICE
                      ? 'service'
                      : undefined
                }
              >
                <Radio id='new-product-type-product-radio-button' value='product'>
                  Infoproduto
                </Radio>
                <Radio id='new-product-type-service-radio-button' value='service'>
                  Serviço
                </Radio>
              </Radio.Group>
              {isInvalid('type') && (
                <span className='mt-1 block text-xs text-red-500'>Selecione o tipo de produto</span>
              )}
            </>
          )}

          <div className='relative z-10 my-4'>
            <label className='mb-1 block text-sm'>
              {isService ? 'Título do serviço *' : 'Título do infoproduto *'}
            </label>
            <Input
              id='new-product-title'
              status={isInvalid('title') ? 'error' : undefined}
              value={product.title}
              onBlur={(e: any) => validateTitleForSave(e.target.value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('title', e.target.value)}
              ref={(ref: any) => setRefField('title', ref)}
              placeholder='Ex: Certificação Especialista em Tráfego Pago'
            />
            <span className={`mt-1 block text-xs ${isInvalid('title') ? 'text-red-500' : 'text-gray-500'}`}>
              {isInvalid('title')
                ? errorFor('title')
                : `O título do seu ${isService ? 'serviço' : 'infoproduto'} deve ser condizente com o que vai ser entregue`}
            </span>
          </div>

          <CharacterCounter
            value={product.description}
            limits={4000}
            minimum={100}
            id='new-product-description'
            error={isInvalid('description')}
            ref={(ref: any) => setRefField('description', ref)}
            helperText={
              isInvalid('description')
                ? errorFor('description')
                : 'A descrição do ' +
                  (isService ? 'serviço' : 'infoproduto') +
                  ' deve deixar claro do que se trata seu ' +
                  (isService ? 'serviço' : 'infoproduto')
            }
            label={isService ? 'Descrição do serviço *' : 'Descrição do infoproduto *'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('description', removeDoubleSpace(e.target.value))
            }
            singleSpace={true}
            placeholder='Ex: Certificação Especialista em Tráfego Pago. Você irá aprender o método testado e aprovado por centenas de empresários para gerar novos clientes para o seu negócio todos os dias.'
          />
        </div>
        <div>
          <label className={`mb-1 block text-sm ${isInvalid('categories') ? 'text-red-500' : ''}`}>Categorias *</label>
          <Select
            id='new-product-categories'
            status={isInvalid('categories') ? 'error' : undefined}
            value={product.getIdCategories()}
            onChange={(value: any) => handleChange('categories', value)}
            placeholder='Informe uma ou mais categorias'
            mode='multiple'
            className='w-full'
            options={categories.map((item: any) => ({ label: item.name, value: item.id }))}
          />
          {isInvalid('categories') && <span className='mt-1 block text-xs text-red-500'>{errorFor('categories')}</span>}
        </div>
        <div>
          <label className={`mb-1 block text-sm ${isInvalid('image_id') ? 'text-red-500' : ''}`}>
            Imagem do {isService ? 'serviço' : 'infoproduto'} *
          </label>
          <CropUploader
            id='new-product-image'
            name='image'
            fileName='image_id'
            uploadType='cnt_foto'
            onChange={handleChange}
          />
          <span className={`mt-1 block text-xs ${errorFor('image_id') ? 'text-red-500' : 'text-gray-500'}`}>
            {isInvalid('image_id')
              ? errorFor('image_id')
              : isService
                ? 'A imagem deve ter boa qualidade e representar o serviço que está sendo oferecido.'
                : 'A imagem deve ter boa qualidade e representar o que está sendo vendido.'}
          </span>
        </div>
        <div className='relative z-10 my-4'>
          <label className='mb-1 block text-sm'>E-mail de suporte *</label>
          <Input
            id='support_email'
            status={isInvalid('support_email') ? 'error' : undefined}
            value={product.support_email}
            disabled={isBlinket}
            onChange={(e: any) => handleChange('support_email', e.target.value)}
          />
          <span className={`mt-1 block text-xs ${isInvalid('support_email') ? 'text-red-500' : 'text-gray-500'}`}>
            {isInvalid('support_email')
              ? errorFor('support_email')
              : 'E-mail que será exibido para seus compradores e que receberá os e-mails de contato do suporte.'}
          </span>
        </div>
      </div>
    </FormContent>
  );
}

export default General;
